<template>
  <div v-if="show_facility_modal"
    class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm">
    <div class="bg-white rounded-md shadow-xl w-full max-w-4xl">
      <!-- Modal Header -->
      <div class="flex justify-between items-center p-6 border-b">
        <h2 class="text-xl font-semibold">{{ isEditing ? 'Edit Facility' : 'Add New Facility' }}</h2>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 18" />
          </svg>
        </button>
      </div>
      <!-- Stepper Navigation -->
      <div class="flex">
        <button @click="currentStep = 'facility-details'"
          class="flex-1 px-6 py-4 font-medium text-sm relative transition-all" :class="{
            'text-orange-400': currentStep === 'facility-details',
            'text-gray-500 bg-gray-100': currentStep !== 'facility-details'
          }">
          <span class="flex items-center justify-center">
            <span class="w-6 h-6 rounded-full border-2 mr-2 flex items-center justify-center text-xs font-bold"
              :class="{
                'bg-white text-orange-400 border-orange-400': currentStep === 'facility-details',
                'bg-transparent border-gray-400': currentStep !== 'facility-details'
              }">
              1
            </span>
            Facility Details
          </span>
        </button>
        <button @click="goToAdminsStep"
          class="flex-1 px-6 py-4 font-medium text-sm relative transition-all" :class="{
            'text-orange-400': currentStep === 'facility-admin',
            'text-gray-500 bg-gray-100': currentStep !== 'facility-admin'
          }">
          <span class="flex items-center justify-center">
            <span class="w-6 h-6 rounded-full border-2 mr-2 flex items-center justify-center text-xs font-bold"
              :class="{
                'bg-white text-orange-400 border-orange-400': currentStep === 'facility-admin',
                'bg-transparent border-gray-400': currentStep !== 'facility-admin'
              }">
              2
            </span>
            Facility Admins
          </span>
        </button>
      </div>
      <!-- Step Content -->
      <div class="p-6 overflow-auto max-h-[75vh]">
        <!-- Facility Details Step -->
        <div v-if="currentStep === 'facility-details'">
          <h3 class="text-lg font-medium mb-6">Facility Details</h3>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="facility_name" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Facility Name
              </label>
              <input id="facility_name" v-model="formData.FacilityName" type="text" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_type" class="block text-sm font-medium text-gray-700">
                Type
              </label>
              <input id="facility_type" v-model="formData.Type" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_owner" class="block text-sm font-medium text-gray-700">
                Owner
              </label>
              <input id="facility_owner" v-model="formData.Owner" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_county" class="block text-sm font-medium text-gray-700">
                County
              </label>
              <input id="facility_county" v-model="formData.County" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_subcounty" class="block text-sm font-medium text-gray-700">
                Sub-County
              </label>
              <input id="facility_subcounty" v-model="formData.SubCounty" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_division" class="block text-sm font-medium text-gray-700">
                Division
              </label>
              <input id="facility_division" v-model="formData.Division" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_location" class="block text-sm font-medium text-gray-700">
                Location
              </label>
              <input id="facility_location" v-model="formData.Location" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_sublocation" class="block text-sm font-medium text-gray-700">
                Sub-Location
              </label>
              <input id="facility_sublocation" v-model="formData.SubLocation" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_constituency" class="block text-sm font-medium text-gray-700">
                Constituency
              </label>
              <input id="facility_constituency" v-model="formData.Constituency" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_nearest_town" class="block text-sm font-medium text-gray-700">
                Nearest Town
              </label>
              <input id="facility_nearest_town" v-model="formData.NearestTown" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_latitude" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Latitude
              </label>
              <input id="facility_latitude" v-model="formData.Latitude" type="number" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_longitude" class="block text-sm font-medium text-gray-700">
                <span class="text-red-500">*</span> Longitude
              </label>
              <input id="facility_longitude" v-model="formData.Longitude" type="number" required
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_commission" class="block text-sm font-medium text-gray-700">
                Commission (%)
              </label>
              <input id="facility_commission" v-model="formData.Commission" type="number"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_maximum_bill_amount" class="block text-sm font-medium text-gray-700">
                Maximum Bill Amount
              </label>
              <input id="facility_maximum_bill_amount" v-model="formData.maximum_bill_amount" type="number"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_kra_pin" class="block text-sm font-medium text-gray-700">
                KRA PIN
              </label>
              <input id="facility_kra_pin" v-model="formData.kra_pin" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_e_tims_no" class="block text-sm font-medium text-gray-700">
                ETims Number
              </label>
              <input id="facility_e_tims_no" v-model="formData.e_tims_no" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_mtiba_username" class="block text-sm font-medium text-gray-700">
                MTIBA Username
              </label>
              <input id="facility_mtiba_username" v-model="formData.mtiba_username" type="text"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label for="facility_mtiba_password" class="block text-sm font-medium text-gray-700">
                MTIBA Password
              </label>
              <input id="facility_mtiba_password" v-model="formData.mtiba_password" type="password"
                class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
          </div>
          <!-- Success Message -->
          <div v-if="successMessage" class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
            {{ successMessage }}
          </div>
          <!-- Error Message -->
          <div v-if="errorMessage" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {{ errorMessage }}
          </div>
          <!-- Navigation Buttons -->
          <div class="flex justify-end mt-8 pt-6 border-t">
            <button @click="closeModal"
              class="px-6 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button @click="goToAdminsStep"
              :disabled="isSubmitting"
              class="px-6 py-2 ml-3 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors flex items-center"
              :class="{ 'opacity-50 cursor-not-allowed': isSubmitting }">
              Next
            </button>
          </div>
        </div>
        <!-- Facility Admins Step -->
        <div v-if="currentStep === 'facility-admin'">
          <h3 class="text-lg font-medium mb-6">Facility Admins</h3>
          <div v-if="isFetchingAdmins" class="flex justify-center items-center">
            <svg class="animate-spin h-5 w-5 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="ml-2 text-gray-600">Loading facility admins...</span>
          </div>
          <div class="space-y-4">
            <div v-for="(admin, index) in formData.admins" :key="index"
              class="grid grid-cols-2 gap-4 items-end">
              <div>
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Username</label>
                <input v-model="admin.Username" type="text" placeholder="Username"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div>
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Email</label>
                <input v-model="admin.Email" type="email" placeholder="Email"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div>
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Phone Number</label>
                <input v-model="admin.PhoneNumber" type="text" placeholder="Phone Number"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div>
                <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Date of Birth</label>
                <input v-model="admin.DateOfBirth" type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div class="flex items-end">
                <div class="flex-1">
                  <label v-if="index === 0" class="block text-sm font-medium text-gray-600 mb-1">Gender</label>
                  <select v-model="admin.Gender"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200">
                    <option value="">Select Gender</option>
                    <option :value="0">Male</option>
                    <option :value="1">Female</option>
                    <option :value="2">Other</option>
                  </select>
                </div>
                <button @click="removeAdmin(index)" v-if="formData.admins.length > 1"
                  class="ml-2 p-2 text-red-500 hover:bg-red-50 rounded">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="flex justify-end">
              <button @click="addAdmin"
                class="mt-4 px-4 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Admin
              </button>
            </div>
            <!-- Success Message -->
            <div v-if="successMessage" class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {{ successMessage }}
            </div>
            <!-- Error Message -->
            <div v-if="errorMessage" class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {{ errorMessage }}
            </div>
            <!-- Navigation Buttons -->
            <div class="flex justify-between mt-8 pt-6 border-t">
              <button @click="currentStep = 'facility-details'"
                class="px-6 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
                Back
              </button>
              <div class="flex space-x-3">
                <button @click="submitFacilityAndAdmins"
                  :disabled="isSubmitting || isFetchingAdmins"
                  class="px-6 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors flex items-center"
                  :class="{ 'opacity-50 cursor-not-allowed': isSubmitting || isFetchingAdmins }">
                  <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                    fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isSubmitting ? 'Submitting...' : isEditing ? 'Update Facility & Admins' : 'Submit Facility & Admins' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { facilitiesAPI } from '@/services';

const props = defineProps({
  show_facility_modal: {
    type: Boolean,
    required: true,
  },
  facility: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close_facility_modal', 'facility-submitted']);

const currentStep = ref('facility-details');
const facilityId = ref(null);
const isSubmitting = ref(false);
const isFetchingAdmins = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);
const isEditing = ref(false);

const formData = ref({
  FacilityName: '',
  Type: '',
  Owner: '',
  County: '',
  SubCounty: '',
  Division: '',
  Location: '',
  SubLocation: '',
  Constituency: '',
  NearestTown: '',
  Latitude: null,
  Longitude: null,
  Commission: 50.0,
  maximum_bill_amount: 0,
  kra_pin: '',
  e_tims_no: '',
  mtiba_username: '',
  mtiba_password: '',
  admins: [{
    Username: '',
    Email: '',
    PhoneNumber: '',
    DateOfBirth: '',
    Gender: null,
    UserType: 'Admin',
    PasswordHash: 'temporary_password',
    IsActive: true,
    LastLoginDate: new Date().toISOString(),
    CreatedBy: 'system',
    CreatedAt: new Date().toISOString(),
  }],
});

const prefillFormData = () => {
  if (props.facility && props.facility.FacilityId) {
    isEditing.value = true;
    facilityId.value = props.facility.FacilityId;
    formData.value = {
      FacilityName: props.facility.FacilityName || '',
      Type: props.facility.Type || '',
      Owner: props.facility.Owner || '',
      County: props.facility.County || '',
      SubCounty: props.facility.SubCounty || '',
      Division: props.facility.Division || '',
      Location: props.facility.Location || '',
      SubLocation: props.facility.SubLocation || '',
      Constituency: props.facility.Constituency || '',
      NearestTown: props.facility.NearestTown || '',
      Latitude: props.facility.Latitude || null,
      Longitude: props.facility.Longitude || null,
      Commission: props.facility.Commission || 50.0,
      maximum_bill_amount: props.facility.maximum_bill_amount || 0,
      kra_pin: props.facility.kra_pin || '',
      e_tims_no: props.facility.e_tims_no || '',
      mtiba_username: props.facility.mtiba_username || '',
      mtiba_password: props.facility.mtiba_password || '',
      admins: props.facility.users?.length
        ? props.facility.users.map(user => ({
            UserId: user.UserId,
            Username: user.Username || '',
            Email: user.Email || '',
            PhoneNumber: user.PhoneNumber || '',
            DateOfBirth: user.DateOfBirth ? new Date(user.DateOfBirth).toISOString().split('T')[0] : '',
            Gender: user.Gender !== null ? user.Gender : null,
            UserType: user.UserType || 'Admin',
            IsActive: user.IsActive !== undefined ? user.IsActive : true,
            LastLoginDate: user.LastLoginDate || new Date().toISOString(),
            CreatedBy: user.CreatedBy || 'system',
            CreatedAt: user.CreatedAt || new Date().toISOString(),
          }))
        : [{
            Username: '',
            Email: '',
            PhoneNumber: '',
            DateOfBirth: '',
            Gender: null,
            UserType: 'Admin',
            PasswordHash: 'temporary_password',
            IsActive: true,
            LastLoginDate: new Date().toISOString(),
            CreatedBy: 'system',
            CreatedAt: new Date().toISOString(),
          }],
    };
  } else {
    isEditing.value = false;
    resetFormData();
  }
};

const addAdmin = () => {
  formData.value.admins.push({
    Username: '',
    Email: '',
    PhoneNumber: '',
    DateOfBirth: '',
    Gender: null,
    UserType: 'Admin',
    PasswordHash: 'temporary_password',
    IsActive: true,
    LastLoginDate: new Date().toISOString(),
    CreatedBy: 'system',
    CreatedAt: new Date().toISOString(),
  });
};

const removeAdmin = (index) => {
  if (formData.value.admins.length > 1) {
    if (isEditing.value && formData.value.admins[index].UserId) {
      formData.value.admins[index]._destroy = true;
    } else {
      formData.value.admins.splice(index, 1);
    }
  }
};

const validateFacilityForm = () => {
  if (!formData.value.FacilityName || formData.value.Latitude === null || formData.value.Longitude === null) {
    errorMessage.value = 'Please fill in all required facility fields (Name, Latitude, Longitude).';
    return false;
  }
  return true;
};

const goToAdminsStep = () => {
  if (!validateFacilityForm()) {
    return;
  }
  errorMessage.value = null;
  console.log('Transitioning to Facility Admins step');
  currentStep.value = 'facility-admin';
};

const submitFacilityAndAdmins = async () => {
  if (!validateFacilityForm()) {
    return;
  }

  const activeAdmins = formData.value.admins.filter(admin => !admin._destroy);
  if (activeAdmins.some(admin => !admin.Username || !admin.Email || !admin.DateOfBirth || admin.Gender === null)) {
    errorMessage.value = 'Please complete all admin fields (Username, Email, Date of Birth, Gender).';
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const payload = {
      Facility: {
        FacilityName: formData.value.FacilityName,
        Type: formData.value.Type,
        Owner: formData.value.Owner,
        County: formData.value.County,
        SubCounty: formData.value.SubCounty,
        Division: formData.value.Division,
        Location: formData.value.Location,
        SubLocation: formData.value.SubLocation,
        Constituency: formData.value.Constituency,
        NearestTown: formData.value.NearestTown,
        Latitude: parseFloat(formData.value.Latitude),
        Longitude: parseFloat(formData.value.Longitude),
        Commission: parseFloat(formData.value.Commission) || 50.0,
        maximum_bill_amount: parseFloat(formData.value.maximum_bill_amount) || 0,
        kra_pin: formData.value.kra_pin,
        e_tims_no: formData.value.e_tims_no,
        mtiba_username: formData.value.mtiba_username,
        mtiba_password: formData.value.mtiba_password,
        admins: formData.value.admins.map(admin => ({
          UserId: admin.UserId,
          Username: admin.Username,
          Email: admin.Email,
          PhoneNumber: admin.PhoneNumber,
          DateOfBirth: admin.DateOfBirth,
          Gender: admin.Gender,
          UserType: admin.UserType || 'Admin',
          PasswordHash: admin.PasswordHash,
          IsActive: admin.IsActive,
          LastLoginDate: admin.LastLoginDate,
          CreatedBy: admin.CreatedBy,
          CreatedAt: admin.CreatedAt,
          _destroy: admin._destroy || false,
        })),
      },
    };

    console.log('Submitting payload:', JSON.stringify(payload, null, 2));

    let response;
    if (isEditing.value && facilityId.value) {
      response = await facilitiesAPI.update(facilityId.value, payload);
    } else {
      response = await facilitiesAPI.create(payload);
      facilityId.value = response.data.data.FacilityId;
    }

    if (!response.data?.success) {
      throw new Error(response.data?.message || (isEditing.value ? 'Failed to update facility and admins' : 'Failed to create facility and admins'));
    }

    emit('facility-submitted', response.data);

    const actions = formData.value.admins.reduce((acc, admin) => {
      if (admin._destroy) {
        acc.deleted = (acc.deleted || 0) + 1;
      } else if (admin.UserId) {
        acc.updated = (acc.updated || 0) + 1;
      } else {
        acc.created = (acc.created || 0) + 1;
      }
      return acc;
    }, { created: 0, updated: 0, deleted: 0 });

    const actionMessages = [];
    if (actions.created) actionMessages.push(`${actions.created} admin(s) created`);
    if (actions.updated) actionMessages.push(`${actions.updated} admin(s) updated`);
    if (actions.deleted) actionMessages.push(`${actions.deleted} admin(s) deleted`);

    successMessage.value = `Success: Facility ${isEditing.value ? 'updated' : 'created'} and ${actionMessages.length > 0 ? actionMessages.join(', ') : 'admins processed'}.`;

    setTimeout(() => {
      successMessage.value = null;
      closeModal();
    }, 2000);

  } catch (err) {
    console.error('Error processing facility and admins:', err);
    errorMessage.value = err.response?.data?.message || err.message || 'Failed to process facility and admins. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit('close_facility_modal');
  resetFormData();
};

const resetFormData = () => {
  formData.value = {
    FacilityName: '',
    Type: '',
    Owner: '',
    County: '',
    SubCounty: '',
    Division: '',
    Location: '',
    SubLocation: '',
    Constituency: '',
    NearestTown: '',
    Latitude: null,
    Longitude: null,
    Commission: 50.0,
    maximum_bill_amount: 0,
    kra_pin: '',
    e_tims_no: '',
    mtiba_username: '',
    mtiba_password: '',
    admins: [{
      Username: '',
      Email: '',
      PhoneNumber: '',
      DateOfBirth: '',
      Gender: null,
      UserType: 'Admin',
      PasswordHash: 'temporary_password',
      IsActive: true,
      LastLoginDate: new Date().toISOString(),
      CreatedBy: 'system',
      CreatedAt: new Date().toISOString(),
    }],
  };
  currentStep.value = 'facility-details';
  facilityId.value = null;
  errorMessage.value = null;
  successMessage.value = null;
  isEditing.value = false;
  isFetchingAdmins.value = false;
};

onMounted(() => {
  console.log('FacilityModal mounted');
  resetFormData();
  prefillFormData();
});

watch(() => props.show_facility_modal, async (newVal) => {
  if (newVal) {
    console.log('Modal opened, resetting state');
    resetFormData();
    
    // If we have a facility with ID, fetch full details for editing
    if (props.facility && props.facility.FacilityId) {
      try {
        const response = await facilitiesAPI.getOne(props.facility.FacilityId);
        if (response.data.success) {
          // Use the full facility data from the API
          const fullFacility = response.data.data;
          isEditing.value = true;
          facilityId.value = fullFacility.FacilityId;
          formData.value = {
            FacilityName: fullFacility.FacilityName || '',
            Type: fullFacility.Type || '',
            Owner: fullFacility.Owner || '',
            County: fullFacility.County || '',
            SubCounty: fullFacility.SubCounty || '',
            Division: fullFacility.Division || '',
            Location: fullFacility.Location || '',
            SubLocation: fullFacility.SubLocation || '',
            Constituency: fullFacility.Constituency || '',
            NearestTown: fullFacility.NearestTown || '',
            Latitude: fullFacility.Latitude || null,
            Longitude: fullFacility.Longitude || null,
            Commission: fullFacility.Commission || 50.0,
            maximum_bill_amount: fullFacility.maximum_bill_amount || 0,
            kra_pin: fullFacility.kra_pin || '',
            e_tims_no: fullFacility.e_tims_no || '',
            mtiba_username: fullFacility.mtiba_username || '',
            mtiba_password: fullFacility.mtiba_password || '',
            admins: fullFacility.users?.length
              ? fullFacility.users.map(user => ({
                  UserId: user.UserId,
                  Username: user.Username || '',
                  Email: user.Email || '',
                  PhoneNumber: user.PhoneNumber || '',
                  DateOfBirth: user.DateOfBirth || '',
                  Gender: user.Gender !== null ? user.Gender : null,
                  UserType: user.UserType || 'Admin',
                  IsActive: user.IsActive !== undefined ? user.IsActive : true,
                  LastLoginDate: user.LastLoginDate || new Date().toISOString(),
                  CreatedBy: user.CreatedBy || 'system',
                  CreatedAt: user.CreatedAt || new Date().toISOString(),
                }))
              : [{
                  Username: '',
                  Email: '',
                  PhoneNumber: '',
                  DateOfBirth: '',
                  Gender: null,
                  UserType: 'Admin',
                  PasswordHash: 'temporary_password',
                  IsActive: true,
                  LastLoginDate: new Date().toISOString(),
                  CreatedBy: 'system',
                  CreatedAt: new Date().toISOString(),
                }],
          };
          return;
        }
      } catch (error) {
        console.error('Error fetching facility details:', error);
      }
    }
    
    // Fallback to using props.facility directly
    prefillFormData();
  }
});

watch(() => props.facility, () => {
  console.log('Facility prop changed, prefilling form');
  prefillFormData();
});
</script>